// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactTypography from "@plumberssupply/App/Components/Common/ContactTypography";
import { CityStatePostalCode } from "@plumberssupply/Overrides/Components/AddressInfoDisplay";
import SignOut from "@plumberssupply/XDL/Icons/SignOut";
import { color, iconLinkCommonStyles, iconStyles, mergeWithCss, xMask } from "@plumberssupply/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

interface CustomWarehouseModel {
    Address: string;
    City: string;
    Email: string;
    Phone: string;
    PostalCode: string;
    State: string;
}

interface MiniSignInProps {
    linkGroups?: {
        title: string;
        links: (
            | {
                  title: string;
                  url: string;
                  type?: string | undefined;
                  id: string;
                  parentId?: string | undefined;
                  excludeFromNavigation?: boolean | undefined;
                  children?: PageLinkModel[] | undefined;
              }
            | undefined
        )[];
    }[];
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

type Props = MiniSignInProps;

export interface MiniMyAccountStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkItems?: {
        wrapper?: InjectableCss;
        linkWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
    personalInfo?: {
        wrapper?: InjectableCss;
        infoWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        subtitle?: TypographyPresentationProps;
        secondSubtitle?: TypographyPresentationProps;
        info?: TypographyPresentationProps;
        contactInfo?: TypographyPresentationProps;
        link?: TypographyPresentationProps;
    };
    actionSection?: {
        wrapper?: InjectableCss;
        signOutButton?: LinkPresentationProps;
    };
}

const miniMyAccountStyles: MiniMyAccountStyles = {
    wrapper: {
        css: css``,
    },
    title: {
        variant: "h5",
        css: css`
            color: ${color("common.ui11")};
            margin-bottom: 0;
        `,
    },
    linkItems: {
        wrapper: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                padding-top: 16px;
                padding-bottom: 16px;
                border-bottom: 1px solid ${color("common.ui7")};
            `,
        },
        title: {
            variant: "h6",
            transform: "capitalize",
            css: css`
                flex-basis: 100%;
                color: ${color("common.ui11")};
            `,
        },
        link: {
            color: "text.main",
            typographyProps: {
                size: 12,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 15px;
                    margin-bottom: 6px;
                `,
            },
        },
        linkWrapper: {
            css: css`
                flex-basis: 50%;
            `,
        },
    },
    personalInfo: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                padding: 16px 0;
                border-bottom: 1px solid ${color("common.ui7")};

                > div {
                    display: flex;
                    justify-content: space-between;
                    flex-wrap: wrap;
                }
            `,
        },
        infoWrapper: {
            css: css`
                max-width: 50%;
                flex-basis: 50%;
            `,
        },
        title: {
            variant: "h5",
            css: css`
                color: ${color("common.ui11")};
                margin-bottom: 0;
            `,
        },
        subtitle: {
            weight: 800,
            ellipsis: true,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
                display: block;
                white-space: break-spaces;
                color: ${color("text")};
            `,
        },
        info: {
            weight: 400,
            ellipsis: true,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
                display: block;
                white-space: break-spaces;
                color: ${color("text")};
            `,
        },
        contactInfo: {
            weight: 400,
            ellipsis: true,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
                display: block;
                margin: 6px 0;
                white-space: break-spaces;
                color: ${color("text")};
            `,
        },
        link: {
            color: "primary",
            size: 12,
            weight: "normal",
            ellipsis: true,
            css: css`
                &:hover {
                    color: ${color("common.ui1")};
                }
            `,
        },
        secondSubtitle: {
            weight: 800,
            ellipsis: true,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
                display: block;
                white-space: break-spaces;
                color: ${color("text")};
                margin-top: 12px;
            `,
        },
    },
    actionSection: {
        wrapper: {
            css: css`
                display: flex;
                padding-top: 16px;
                width: 100%;
            `,
        },
        signOutButton: {
            ...mergeWithCss(iconLinkCommonStyles(SignOut), { ...iconStyles }),
        },
    },
};

const MiniMyAccount: FC<Props> = ({ linkGroups, onSignOutHandler, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(miniMyAccountStyles, extendedStyles));
    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const {
        currentBillTo,
        isPunchOutSession,
        pickupWarehouse,
        changeCustomerPageUrl,
        currentLocation,
        userSetupPageUrl,
    } = useAppSelector(state => {
        return {
            currentBillTo: getCurrentBillToState(state).value,
            isPunchOutSession: getIsPunchOutSession(state),
            pickupWarehouse: JSON.parse(
                getSession(state).properties.userDefaultWarehouse || "{}",
            ) as CustomWarehouseModel,
            changeCustomerPageUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            currentLocation: getLocation(state),
            userSetupPageUrl: getPageLinkByPageType(state, "UserSetupPage")?.url,
        };
    });

    const history = useHistory();
    const handleChangeCustomerPageRedirect = () => {
        const returnUrl = currentLocation.pathname + currentLocation.search;
        if (
            returnUrl === "/" ||
            currentLocation.pathname === changeCustomerPageUrl ||
            (userSetupPageUrl && returnUrl.includes(userSetupPageUrl))
        ) {
            history.push(`${changeCustomerPageUrl}`);
        } else {
            history.push(`${changeCustomerPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
        }
        hideTippy();
    };

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                    {linkGroup.title && <Typography {...styles.linkItems?.title}>{linkGroup.title}</Typography>}
                    {linkGroup.links.map(
                        link =>
                            link?.url && (
                                <StyledWrapper key={`${link?.title}_${link?.url}`} {...styles.linkItems?.linkWrapper}>
                                    {link.url === changeCustomerPageUrl ? (
                                        <Link {...styles.linkItems?.link} onClick={handleChangeCustomerPageRedirect}>
                                            {link?.title}
                                        </Link>
                                    ) : (
                                        <Link href={link?.url} {...styles.linkItems?.link} onClick={hideTippy}>
                                            {link?.title}
                                        </Link>
                                    )}
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            );
        }
        return null;
    });

    const branchPhone = pickupWarehouse?.Phone || "";
    const branchCity = pickupWarehouse?.City || "";
    const branchState = pickupWarehouse?.State || "";
    const branchPostalcode = pickupWarehouse?.PostalCode || "";
    const branchEmail = pickupWarehouse?.Email || "";
    const branchAddress = pickupWarehouse?.Address || "";

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>{translate("My Account")}</Typography>
            {linksSection}
            <StyledWrapper {...styles.personalInfo?.wrapper}>
                <Typography {...styles.linkItems?.title}>{translate("Profile Information")}</Typography>
                <StyledWrapper>
                    {currentBillTo && (
                        <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                            <Typography {...styles.personalInfo?.subtitle}>{translate("Customer Info")}</Typography>
                            <Typography {...styles.personalInfo?.info}>{`${currentBillTo?.companyName}`}</Typography>
                            <Typography {...styles.personalInfo?.secondSubtitle}>{`${translate(
                                "Customer #",
                            )}:`}</Typography>
                            <Typography {...styles.personalInfo?.info}>{currentBillTo?.customerNumber}</Typography>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                        <Typography {...styles.personalInfo?.subtitle}>{translate("My Branch Info")}</Typography>
                        {branchAddress && (
                            <ContactTypography icon type="address" {...styles.personalInfo?.info}>
                                {branchAddress}
                            </ContactTypography>
                        )}

                        {(branchCity || branchState || branchPostalcode) && (
                            <ContactTypography icon={!branchAddress} type="address" {...styles.personalInfo?.info}>
                                <CityStatePostalCode
                                    styles={{ cityStatePostalCodeText: styles.personalInfo?.info }}
                                    city={branchCity}
                                    state={branchState}
                                    postalCode={branchPostalcode}
                                />
                            </ContactTypography>
                        )}

                        {branchPhone && (
                            <ContactTypography icon targetValue={branchPhone} type="phone">
                                {xMask.phoneNumber(branchPhone)}
                            </ContactTypography>
                        )}
                        {branchEmail && (
                            <ContactTypography icon targetValue={branchEmail} type="email">
                                {branchEmail}
                            </ContactTypography>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.actionSection?.wrapper}>
                <Link
                    {...styles.actionSection?.signOutButton}
                    onClick={() => {
                        onSignOutHandler?.();
                        hideTippy();
                    }}
                    aria-label="signOut"
                    data-test-selector="headerSignOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default MiniMyAccount;
